import type { GameScore } from '../../types/game';

export interface ScoreDisplayProps {
  score: GameScore;
  context: CanvasRenderingContext2D;
  position: { x: number; y: number };
  color?: string;
  font?: string;
  fontSize?: number;
  textAlign?: 'left' | 'center' | 'right';
  spacing?: number;
  labels?: {
    player?: string;
    ai?: string;
  };
  layout?: 'horizontal' | 'vertical';
}

/**
 * ScoreDisplay atomic component - renders game score on canvas
 */
export class ScoreDisplay {
  private props: ScoreDisplayProps;

  constructor(props: ScoreDisplayProps) {
    this.props = props;
  }

  /**
   * Update score display properties
   */
  public updateProps(props: Partial<ScoreDisplayProps>): void {
    this.props = { ...this.props, ...props };
  }

  /**
   * Render the score display on canvas
   */
  public render(): void {
    const { 
      score, 
      context, 
      position,
      color = '#ffffff',
      font = 'Arial',
      fontSize = 12,
      textAlign = 'left',
      spacing = 100,
      labels = { player: 'Player', ai: 'CPU' },
      layout = 'horizontal'
    } = this.props;
    
    context.save();
    
    // Set text properties
    context.fillStyle = color;
    context.font = `${fontSize}px ${font}`;
    context.textAlign = textAlign;
    
    if (layout === 'horizontal') {
      this.renderHorizontalLayout(context, score, position, spacing, labels);
    } else {
      this.renderVerticalLayout(context, score, position, spacing, labels);
    }
    
    context.restore();
  }

  /**
   * Render horizontal layout
   */
  private renderHorizontalLayout(
    context: CanvasRenderingContext2D,
    score: GameScore,
    position: { x: number; y: number },
    spacing: number,
    labels: { player?: string; ai?: string }
  ): void {
    // Player score on the left
    const playerText = `${labels.player || 'Player'}: ${score.player}`;
    context.fillText(playerText, position.x, position.y);
    
    // AI score on the right
    const aiText = `${labels.ai || 'CPU'}: ${score.ai}`;
    context.fillText(aiText, position.x + spacing, position.y);
  }

  /**
   * Render vertical layout
   */
  private renderVerticalLayout(
    context: CanvasRenderingContext2D,
    score: GameScore,
    position: { x: number; y: number },
    spacing: number,
    labels: { player?: string; ai?: string }
  ): void {
    // Player score on top
    const playerText = `${labels.player || 'Player'}: ${score.player}`;
    context.fillText(playerText, position.x, position.y);
    
    // AI score below
    const aiText = `${labels.ai || 'CPU'}: ${score.ai}`;
    context.fillText(aiText, position.x, position.y + spacing);
  }

  /**
   * Get text bounds for layout calculations
   */
  public getTextBounds(): { width: number; height: number } {
    const { 
      score, 
      context, 
      font = 'Arial',
      fontSize = 12,
      labels = { player: 'Player', ai: 'CPU' },
      layout = 'horizontal'
    } = this.props;
    
    context.save();
    context.font = `${fontSize}px ${font}`;
    
    const playerText = `${labels.player || 'Player'}: ${score.player}`;
    const aiText = `${labels.ai || 'CPU'}: ${score.ai}`;
    
    const playerMetrics = context.measureText(playerText);
    const aiMetrics = context.measureText(aiText);
    
    context.restore();
    
    if (layout === 'horizontal') {
      return {
        width: Math.max(playerMetrics.width, aiMetrics.width),
        height: fontSize
      };
    } else {
      return {
        width: Math.max(playerMetrics.width, aiMetrics.width),
        height: fontSize * 2
      };
    }
  }

  /**
   * Get individual score text
   */
  public getScoreText(type: 'player' | 'ai'): string {
    const { score, labels = { player: 'Player', ai: 'CPU' } } = this.props;
    
    if (type === 'player') {
      return `${labels.player || 'Player'}: ${score.player}`;
    } else {
      return `${labels.ai || 'CPU'}: ${score.ai}`;
    }
  }

  /**
   * Check if point is over score display (for interaction)
   */
  public containsPoint(x: number, y: number): boolean {
    const bounds = this.getTextBounds();
    const { position } = this.props;
    
    return x >= position.x && 
           x <= position.x + bounds.width &&
           y >= position.y - bounds.height && 
           y <= position.y;
  }
}

/**
 * Factory function to create ScoreDisplay instances
 */
export function createScoreDisplay(props: ScoreDisplayProps): ScoreDisplay {
  return new ScoreDisplay(props);
}

/**
 * Static rendering function for functional-style usage
 */
export function renderScoreDisplay(props: ScoreDisplayProps): void {
  const scoreDisplay = new ScoreDisplay(props);
  scoreDisplay.render();
} 